import { errorToast } from "@/shared/lib/apiErrors";
import { SKU, imageVariant } from "@/entities/pricing/model/priceBook";
import { acquireQuoteId } from "@/entities/pricing/model/useQuote";
import { usePriceBook } from "@/entities/pricing/model/usePriceBook";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ChevronLeft,
  ChevronRight,
  Download,
  Flame,
  Info,
  Loader2,
  Eye,
  Save,
  RotateCcw,
  Layers,
} from "lucide-react";

import { Button } from "@/shared/ui/button";
import { Card, CardContent } from "@/shared/ui/card";
import { ToastAction } from "@/shared/ui/toast";
import { useToast } from "@/shared/hooks/use-toast";
import { useBrandAssets } from "@/entities/brand-asset";
import { cn } from "@/shared/lib/utils";
import { imageExtFromUrl, triggerDownload } from "@/shared/lib/download";
import {
  posterService,
  type FilterConfig,
  type OverlayConfig,
  type PosterAssetDto,
  type PosterTaskDto,
  type TemplateMeta,
} from "../../../api/posterService";
import {
  DEFAULT_OVERLAY,
  type OverlayConfig as OverlayConfigV6,
  type OverlayItem as OverlayItemV6,
} from "../../../api/posterTemplateService";

import { bakedOverlayOf, withBakedMarkers } from "./bakedOverlay";
import { stampedOverlayOf } from "./stampedOverlay";
import QRCode from "qrcode";
import { BrandVariantsDialog } from "./BrandVariantsDialog";
import { AttachLogoButton, AttachQrButton } from "./AttachBrandButtons";
import { PosterCanvas, type BrandPreview } from "../PosterCanvas";
import { useFitBox } from "@/shared/hooks/useFitBox";
import { MediaPreviewLightbox } from "@/entities/media-asset/ui/MediaPreviewLightbox";
import { filterToCss } from "../canvas/filterCss";
import { DragOverlayCanvas } from "@/shared/ui/overlay/DragOverlayCanvas";
import { AppearanceTab } from "./AppearanceTab";
import { I2IEditTab } from "./I2IEditTab";
import { RegenerateTab } from "./RegenerateTab";
import { VersionSwitcher } from "./VersionSwitcher";
import { buildVersionChain } from "./versionChain";

// V26.4 (2026-05-14) — IA 收口:成品页 (PosterAssetEditPage) 砍掉, 全部编辑能力进
// 详情页 AssetEditor 三 tab,grid 卡片只剩下载。
//   appearance (画面调整) — 拖位置 + 滤镜, 免费即时, 2~3 秒合成
//   edit       (AI 改图)  — i2i image_edit, 保留构图改局部, 30~60 秒
//   redraw     (AI 重画)  — t2i semantic regen, 推翻重画, 30~90 秒
type TabKey = "appearance" | "edit" | "redraw";

/**
 * V6 fix4: asset.overlayConfig 后端现在存 V6 shape ({version,logo,qr})。
 * 老 v2.9 AssetEditor 里的 OverlayConfig 是 anchor shape,二者不兼容。
 * 这里 cast + 校验 version=1, 失败兜底 DEFAULT_OVERLAY。
 *
 * V6 fix4 (later): hasLogo/hasQr 标志按 brand 实际有没有 clamp overlay —
 * 用户没上传 logo 时,不管 storage 里 stale 的 bbox 是什么,前端一律按 logo=null
 * 展示(也别保存非空 bbox 回去),避免「拖了半天后端啥也没叠」的迷惑 UX。
 */
function readOverlayV6(raw: unknown, hasBrandLogo: boolean, hasBrandQr: boolean): OverlayConfigV6 {
  const fallbackLogo = hasBrandLogo ? DEFAULT_OVERLAY.logo : null;
  const fallbackQr = hasBrandQr ? DEFAULT_OVERLAY.qr : null;
  if (!raw || typeof raw !== "object") {
    return { version: 1, logo: fallbackLogo, qr: fallbackQr };
  }
  const o = raw as Partial<OverlayConfigV6>;
  if (o.version !== 1) {
    return { version: 1, logo: fallbackLogo, qr: fallbackQr };
  }
  // V37 (2026-05-18) — override 字段透传;有 override (logoUploadId / qrCodeUploadId / qrCodeUrl)
  // 时不再按 hasBrandLogo / hasBrandQr clamp 掉 logo / qr 位置,保留用户挂载状态。
  const hasLogoOverride = !!o.logoUploadId;
  const hasQrOverride = !!o.qrCodeUploadId || !!o.qrCodeUrl;
  // 1006:出图时已烙进画面的元素(bakedOverlayOf)编辑态一律置空 —— 不给拖框、保存不再叠(防双 logo)。
  return withBakedMarkers(raw, {
    version: 1,
    logo: hasBrandLogo || hasLogoOverride ? ((o.logo ?? null) as OverlayItemV6 | null) : null,
    qr: hasBrandQr || hasQrOverride ? ((o.qr ?? null) as OverlayItemV6 | null) : null,
    logoUploadId: o.logoUploadId ?? null,
    qrCodeMode: o.qrCodeMode ?? null,
    qrCodeUploadId: o.qrCodeUploadId ?? null,
    qrCodeUrl: o.qrCodeUrl ?? null,
  });
}

/**
 * 详情页主预览 canvas 的真实宽高比。
 * actualWidth/actualHeight 优先(asset 级真实尺寸),否则解析 task.ratio,最后 1.0 兜底。
 */
/**
 * V37 — DragOverlayCanvas 预览 logoUrl 解析:
 * - overlay 有 logoUploadId override → 查 brand_assets.logos[].objectKey 匹配的 signedUrl
 * - 否则 null,caller fallback 到 task.logoUrl
 */
function resolveLogoPreviewUrl(
  overlay: OverlayConfigV6,
  brandAssets: import("@/entities/brand-asset").BrandAssets | undefined
): string | null {
  if (!overlay.logoUploadId || !brandAssets) return null;
  const hit = brandAssets.logos.find((l) => l.objectKey === overlay.logoUploadId);
  return hit?.signedUrl ?? null;
}

/**
 * V37 — DragOverlayCanvas 预览 qrUrl 解析:
 * - UPLOAD override → 查 signedUrl
 * - URL override → 由 caller 用 QRCode lib 生成 dataURL(本组件返 null,caller 异步生)
 * - 否则 fallback task.qrCodeUrl
 */
function resolveQrPreviewUrl(
  overlay: OverlayConfigV6,
  brandAssets: import("@/entities/brand-asset").BrandAssets | undefined
): string | null {
  if (overlay.qrCodeMode === "UPLOAD" && overlay.qrCodeUploadId && brandAssets) {
    const hit = brandAssets.qrs.find((q) => q.objectKey === overlay.qrCodeUploadId);
    return hit?.signedUrl ?? null;
  }
  // URL 模式:caller 自己生 dataURL,这里返 null。本期 URL-mode QR 预览暂不支持。
  return null;
}

function aspectFromRatio(r: string | undefined | null): number | null {
  if (!r) return null;
  const m = r.match(/^(\d+):(\d+)$/);
  if (!m) return null;
  const w = Number(m[1]);
  const h = Number(m[2]);
  if (!w || !h) return null;
  return w / h;
}

/**
 * v2.9 详情页核心编辑器。
 *
 * 左侧:PosterCanvas WYSIWYG,调摆放/画面实时反映
 * 右侧:三 Tab (摆放 / 画面 / 重画) + 一键美化大按钮
 *
 * 保存(摆放/画面):走 PATCH /assets/:id/adjustment,**不扣费**
 * 重画(Semantic):走 POST /assets/:id/regenerate,**扣 1 张图 + LLM token**
 *
 * "按住对比原图":鼠标按住时 canvas 退到 base 图 + 模版 anchors 默认布局,松手恢复。
 */
export interface AssetEditorProps {
  task: PosterTaskDto;
  asset: PosterAssetDto;
  allAssets: PosterAssetDto[];
  templates: TemplateMeta[];
  brand: BrandPreview;
  onSelectVersion: (assetId: number) => void;
  /** s15.6: 保存调整 / 重画后强制重拉 task + assets, 让 props.asset 更新驱动 canvas 同步. */
  onRefresh?: () => Promise<void> | void;
  /** #12 (2026-07-04): 付费改图/重画会让终态任务重回 RUNNING, 调此恢复轮询直到新 SUCCESS, 防"改图丢图". */
  onRestart?: () => void;
  /** V26 (2026-05-14) — 保存/重画 mutation pending 状态外溢给父组件, 让下方 grid 卡片下载能在合成期间灰掉, 防用户拿旧签名 URL 下到原图。 */
  onSavingChange?: (pending: boolean) => void;
  /**
   * 2026-07-28 W2 — 成品条插槽(详情页传 PosterResultStrip + 组图续出条)。
   *
   * ★为什么非要塞进编辑器内部:成品条以前在页面最底部,用户在主画面看完一张要切下一张
   * 得滚到 1300px 以下点一下再滚回来,组图 / 套图每切一次滚两趟。挂进编辑器 = 切图零滚动。
   *
   * ★2026-08-07 位置**留在画布正下方**,但压到 96px 高(原 148)。
   *   中间试过搬进 400px 的操作台顶部(想把高度还给画面),真页面上被 6 张的套图否掉:
   *   卡宽按各自真实画幅反算(9:16 才 54px 宽、16:9 有 171px),六张横着排 ~700px,
   *   400 的列里**只露得出三张半**,后面两张要横向滚 —— 而套图这条带子的活就是
   *   「六个画幅摆一起比」,看不全等于没有。902 宽的看图台列一行装得下,所以它归这儿。
   *   高度的账改从别处省:148→96,单图任务整条不渲染(见 PosterResultStrip)。
   */
  tray?: ReactNode;
  /** 主画面左上角标(组图:这张在讲哪个爆点)。不给则不渲染。 */
  stageBadge?: string;
  /*
   * ★`wide` 已删(2026-08-07 版面重构)。
   *
   * 它当初存在的理由是「16:9 被 400px 固定右列挤剩一条缝」—— 那是**宽度驱动**取景下的
   * 必然:画布 `width:100%`,右列吃掉 400 之后剩下的宽度就是画面的全部尺寸。
   * 改成盒子驱动(`PosterCanvas fit`)之后,16:9 在 902 宽的 Stage 里只有 507 高,
   * 上下自然留白,右列一点不碍事 —— 这个例外没有了存在的前提。
   *
   * 留这段注释是因为「宽图为什么不再特殊」是下一个人一定会问的问题。
   */
}

export function AssetEditor({
  task,
  asset,
  allAssets,
  templates,
  brand,
  onSelectVersion,
  onRefresh,
  onRestart,
  onSavingChange,
  tray,
  stageBadge,
}: AssetEditorProps) {
  const { t } = useTranslation("poster");
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [tab, setTab] = useState<TabKey>("appearance");
  // 0713 批量品牌变体弹窗(一行一张图,位置沿用当前 v6Overlay 摆放)
  const [variantsOpen, setVariantsOpen] = useState(false);

  // V6 fix4 (later): brand 实际是否有 logo / QR — 没有就不该展示拖拽框,也别 fallback 示例图。
  const hasBrandLogo = !!task.logoUrl;
  const hasBrandQr = !!task.qrCodeUrl;
  // 1006:出图时已烙进画面的 logo/二维码 —— 以 assetMeta 的出图叠加记录为准,不看 logoUrl。
  const baked = useMemo(
    () => bakedOverlayOf(asset.currentOverlayConfig),
    [asset.currentOverlayConfig]
  );

  // V37 (2026-05-18) — 拿租户 brand_assets 用来:(a) 详情页临时挂载 selector,
  // (b) override 已设时,根据 logoUploadId / qrCodeUploadId 查 signedUrl 喂 DragOverlayCanvas 预览。
  const { data: brandAssets } = useBrandAssets();

  // 1006:「加品牌」stamped 版本 —— 已叠的 logo/码还原成可拖的现有元素(底图换成 editBaseUrl 素图,见下)。
  //   画布比例只取 asset 真实像素 / task.ratio(不等量实图):量实图晚到会让初值重算、冲掉用户没存的拖动。
  const stampAspect =
    (asset.actualWidth && asset.actualHeight ? asset.actualWidth / asset.actualHeight : null) ??
    aspectFromRatio(task.ratio) ??
    1;
  const persistedV6 = useMemo(
    () =>
      stampedOverlayOf(asset, brandAssets, stampAspect) ??
      readOverlayV6(asset.currentOverlayConfig, hasBrandLogo, hasBrandQr),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- asset 只取这几项
    [
      asset.stampedBrand,
      asset.currentOverlayConfig,
      asset.actualWidth,
      asset.actualHeight,
      brandAssets,
      stampAspect,
      hasBrandLogo,
      hasBrandQr,
    ]
  );

  const persistedV6Key = JSON.stringify(persistedV6);

  // Phase D (2026-05-16) — 优先读新字段 currentOverlayConfig / currentFilterConfig,
  // 旧 overlayConfig / filterConfig 兜底过渡期(backend 短期仍并发出)。
  const initialOverlay = asset.currentOverlayConfig ?? {};
  const initialFilter = asset.currentFilterConfig ?? {};
  // 编辑层 state — 用户改但还没保存的 overlay/filter。挂载时从 asset 当前值初始化。
  const [overlay, setOverlay] = useState<OverlayConfig>(initialOverlay);
  const [filter, setFilter] = useState<FilterConfig>(initialFilter);
  // V6 fix4: 拖拽 tab 自己的 V6-shape overlay state(独立于上面 legacy overlay)
  const [v6Overlay, setV6Overlay] = useState<OverlayConfigV6>(() => persistedV6);
  const [comparing, setComparing] = useState(false);
  const [viewerOpen, setViewerOpen] = useState(false); // BP-01 看原图

  // 切换 asset 版本时重新挂初值
  useEffect(() => {
    const nextOverlay = asset.currentOverlayConfig ?? {};
    const nextFilter = asset.currentFilterConfig ?? {};
    setOverlay(nextOverlay);
    setFilter(nextFilter);
    setV6Overlay(persistedV6);
    // ★按内容不按引用:品牌库查询每次重取都是新对象,按引用会把用户没存的拖动冲掉(甚至来回重渲染)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asset.id, asset.currentOverlayConfig, asset.currentFilterConfig, persistedV6Key]);

  // V28.1 (2026-05-16) — 拖 logo/QR / 调滤镜全走 DragOverlayCanvas 纯前端 CSS overlay,
  // 不再 debounce 调 /preview-overlay (省 ai-service 积分 + 体感更顺,~300ms 网络抖动消失)。
  // 用户点"保存并重新合成"才触发 backend → /compose + /preview 重生 final + thumb 覆盖 MinIO。

  // asset.templateId 优先;缺失 fallback 首个,避免 anchors 默认值跟实际模板对不上。
  const template: TemplateMeta = useMemo(() => {
    if (templates.length === 0) {
      throw new Error("templates 列表为空");
    }
    if (asset.templateId) {
      const hit = templates.find((t) => t.id === asset.templateId);
      if (hit) return hit;
    }
    return templates[0];
  }, [asset.templateId, templates]);

  const versions = useMemo(() => buildVersionChain(allAssets, asset.id), [allAssets, asset.id]);

  /**
   * 兄弟张(按 seq)—— 与版本链**正交**:版本链回答「这一张的第几版」,
   * 这里回答「第几张」。组图 4 张、套图 3~6 张,用户要在张与张之间来回看。
   *
   * ★2026-08-11 加(测试反馈 C-02):「点击第一张图,这块框出来地方有一个下一张的按钮
   *   感觉会比较方便一点」。此前切张只能去底部缩略条点 —— 主画面上没有任何"还有下一张"
   *   的提示,单张任务和多张任务的看图台长得一模一样。
   * ★每个 seq 只取**最新版**:同一张改过图的话 allAssets 里有多条,
   *   翻页翻到旧版本会让人以为改图丢了。
   */
  const siblings = useMemo(() => {
    const latestBySeq = new Map<number, (typeof allAssets)[number]>();
    for (const a of allAssets) {
      const cur = latestBySeq.get(a.seq);
      if (!cur || a.id > cur.id) latestBySeq.set(a.seq, a);
    }
    return [...latestBySeq.values()].sort((x, y) => x.seq - y.seq);
  }, [allAssets]);
  const siblingPos = siblings.findIndex((s) => s.seq === asset.seq);
  const prevSibling = siblingPos > 0 ? siblings[siblingPos - 1] : null;
  const nextSibling =
    siblingPos >= 0 && siblingPos < siblings.length - 1 ? siblings[siblingPos + 1] : null;

  // Phase D — dirty 比对当前 state 跟 asset 的"持久化"值,新字段优先。
  const persistedOverlay = asset.currentOverlayConfig ?? {};
  const persistedFilter = asset.currentFilterConfig ?? {};
  const dirty =
    JSON.stringify(overlay) !== JSON.stringify(persistedOverlay) ||
    JSON.stringify(filter) !== JSON.stringify(persistedFilter);
  // V6 fix4: 拖拽 tab 自己的 dirty 判定
  const v6Dirty = JSON.stringify(v6Overlay) !== JSON.stringify(persistedV6);

  // V6 fix4 (2026-05-12): legacy adjustMut (PATCH /adjustment) 已废弃 — V6 端点已支持
  // 一并落 overlay + filter,画面 tab 现在统一走 combinedSaveMut。如果将来要单独调 filter,
  // 老 posterService.updateAssetAdjustment 客户端方法仍保留(后端 /adjustment 端点也还在),
  // 现在 AssetEditor 不再调用它。

  // V6 fix4 (2026-05-12): 画面 tab 一个 save 落 overlay (V6 shape) + filter。
  // 走新 V6 PATCH /tasks/.../assets/.../overlay,backend 现在接 { overlay, filter? } 两字段。
  // 同步重合成 + 重生缩略图(F1 修)+ 返回 fresh URLs(filename 带 -v{version},F7 防 OS 缓存)。
  //
  // V26 (2026-05-14) — onRefresh 改 await(原本 void 触发):usePosterTaskPolling
  // 用 local useState 不走 React Query,invalidateQueries 是 no-op。原代码 toast
  // 立刻弹"立即下载",但下方 grid 里 PosterAssetCard 的 downloadUrl 还是 onRefresh
  // 完成前的旧值,用户在 toast 出现到 refetch 完成之间点 grid 卡片下载,签名 URL
  // 指向 recompose 前的 objectKey,下载到的还是原图。把 refresh 串到 mutationFn 里,
  // 等本地 state 落到新 asset 再 resolve mutation,toast / 下方按钮一起拿到新 URL。
  const combinedSaveMut = useMutation({
    mutationFn: async () => {
      // Phase D (2026-05-16) — 走新 PUT /assets/{id}/overlay,body {overlayConfig, filterConfig}。
      // backend 持久化 + 异步重生 thumb(MQ),返 200 no body。onRefresh 拉最新 asset 拿新 thumbUrl。
      await posterService.saveAssetOverlay(asset.id, {
        overlayConfig: v6Overlay,
        filterConfig: filter,
      });
      await onRefresh?.();
    },
    onSuccess: () => {
      toast({
        title: t("toast.saveAndRecompose"),
        action: (
          <ToastAction altText={t("toast.downloadNow")} onClick={() => void downloadCurrentAsset()}>
            {t("toast.downloadNow")}
          </ToastAction>
        ),
      });
      queryClient.invalidateQueries({ queryKey: ["poster-assets", task.id] });
    },
    onError: (e: unknown) => {
      toast(errorToast(e, { fallbackTitle: t("toast.saveFailed") }));
    },
  });

  // cutover:downloadAsset 返 presigned {url},直接 triggerDownload(MinIO 已签 disposition)。
  const downloadCurrentAsset = async () => {
    try {
      const url = await posterService.downloadAsset(asset.id);
      triggerDownload(url, `poster-${asset.taskId}-${asset.seq}.${imageExtFromUrl(url)}`);
    } catch (e) {
      toast(errorToast(e, { fallbackTitle: t("toast.saveFailed") }));
    }
  };

  // V26.4 (2026-05-14) — 单 regenMut 同时服务 AI 改图 (mode=edit) + AI 重画
  // (mode=semantic),mutate 时按 tab 注入 mode。toast 文案按 mode 派,30-60s / 30-90s。
  // ★修复波 0926:AI 改图 / AI 重画的按钮要亮出这一张的价(与下面 regen 报价单同一组参数:
  //   task.provider × task.resolutionTier,qty 1)。价目表没到货就不出数字。
  const { book: priceBook } = usePriceBook();
  const regenCredits = task.provider
    ? priceBook.unit(
        SKU.posterImage,
        imageVariant(task.provider, (task.resolutionTier ?? "2K") as string)
      )
    : null;

  const regenMut = useMutation({
    mutationFn: (body: {
      presetId?: string;
      instruction?: string;
      mode?: "edit" | "semantic";
      replace: boolean;
    }) =>
      // ★ 批 L:单图重生是**真出一张图、真扣一笔钱**,和建单一样要有自己的报价单。
      //   后端把它绑到 `{taskId}:regen:{assetId}:{seed}` —— 这条路上一次没绑,
      //   结算查不到单就会静默回退到旧的成本 ×10 口径(用户被按 6.25 倍扣)。
      //   报价失败不阻断(返 null),理由同其余几条口。
      acquireQuoteId(
        task.provider
          ? {
              skuCode: SKU.posterImage,
              variantKey: imageVariant(task.provider, (task.resolutionTier ?? "2K") as string),
              qty: 1,
            }
          : null
      ).then((quoteId) =>
        posterService.regenerateAsset(task.id, asset.id, {
          ...body,
          ...(quoteId ? { quoteId } : {}),
        })
      ),
    onSuccess: (_data, body) => {
      const isI2i = body.mode === "edit";
      toast({
        title: isI2i ? t("toast.i2iSubmitted") : t("toast.regenSubmitted"),
        description: isI2i ? t("toast.i2iSubmittedDesc") : t("toast.regenSubmittedDesc"),
      });
      queryClient.invalidateQueries({ queryKey: ["poster-assets", task.id] });
      // #12:任务已回 RUNNING,恢复轮询直到新 SUCCESS(否则 doneRef 已停轮询,改图结果拉不回)。
      onRestart?.();
    },
    onError: (e: unknown, body) => {
      const isI2i = body.mode === "edit";
      toast(
        errorToast(e, { fallbackTitle: isI2i ? t("toast.i2iFailed") : t("toast.regenFailed") })
      );
    },
  });

  // V26 (2026-05-14) — 把内部 mutation pending 上抛, 让父组件能在合成期间禁掉
  // grid 卡片下载,免得用户拿到旧 signed URL 下到原图。
  //
  // V26.3 (2026-05-14) — 只 propagate combinedSaveMut (拖拽合成,sync) 的 pending,
  // regenMut 是 async + 末尾占位卡已有显式 spinner,如果把 regenMut.isPending 也上
  // 抛会导致 PosterAssetCard.busy=true → 每张卡的"重新生成"图标都转圈(用户报
  // "两张一起在转圈" 的根因),且 regen 提交跟 grid 卡片下载没直接 race 关系。
  useEffect(() => {
    if (!onSavingChange) return;
    onSavingChange(combinedSaveMut.isPending);
  }, [combinedSaveMut.isPending, onSavingChange]);

  function handleResetAdjustments() {
    setOverlay(asset.currentOverlayConfig ?? {});
    setFilter(asset.currentFilterConfig ?? {});
  }

  // "按住对比原图" 时 canvas 用 base 图 + 模版默认 anchors,filter / overlay 全清。
  const previewOverlay = comparing ? {} : overlay;
  const previewFilter = comparing ? null : filter;
  // V28.1 — Canvas 显示 asset.baseUrl 4K PNG 原图;拖 logo/QR 由 DragOverlayCanvas 纯前端 CSS 叠加。
  const baseAssetUrl = asset.baseUrl ?? null;
  const baseUrl = baseAssetUrl;
  const missingBase = !baseAssetUrl;
  // 1006:画面调整的底图 —— stamped 版本用 api 回溯的 origin 素图(与保存/预览合成同一张),其余就是 baseUrl。
  const appearanceBaseUrl = asset.editBaseUrl ?? baseUrl;
  // 1006:AI 改图 / AI 重画看的是这张图本身(AI 改图的输入)。图上已有品牌层(出图时烙的 / 加品牌叠的)
  //   就不再在上面盖一层 logo/码 —— 否则预览两个 logo(演示机复测 042928-12)。
  const stamped = asset.stampedBrand != null;
  const canvasBrand: BrandPreview = useMemo(
    () => ({
      ...brand,
      logoUrl: baked.logo || stamped ? null : brand.logoUrl,
      qrcodeUrl: baked.qr || stamped ? null : brand.qrcodeUrl,
    }),
    [brand, baked.logo, baked.qr, stamped]
  );
  // URL 模式二维码(加品牌叠的 / 用户挂的)预览:前端现编码,与 compositor 同纠错级
  const urlQr = v6Overlay.qrCodeMode === "URL" ? (v6Overlay.qrCodeUrl ?? null) : null;
  const [urlQrDataUrl, setUrlQrDataUrl] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    if (!urlQr) {
      setUrlQrDataUrl(null);
      return;
    }
    QRCode.toDataURL(urlQr, { errorCorrectionLevel: "M", margin: 1, width: 512 })
      .then((d) => {
        if (!cancelled) setUrlQrDataUrl(d);
      })
      .catch(() => {
        if (!cancelled) setUrlQrDataUrl(null);
      });
    return () => {
      cancelled = true;
    };
  }, [urlQr]);

  /**
   * F-04(0810 反馈):套图里每张的画幅各不相同,而 actualWidth/Height 可能为空 ——
   * 比例一旦落到 task.ratio/1:1,盒子与真图不符,底图 object-cover 居中裁切把顶部
   * (主标题区)切掉,「在线查看被裁、下载完整」。补一级「量实图」:图本身永远是真相。
   */
  const [naturalAspect, setNaturalAspect] = useState<number | null>(null);
  useEffect(() => {
    setNaturalAspect(null);
    if (!baseAssetUrl) return;
    const probe = new Image();
    probe.onload = () => {
      if (probe.naturalWidth && probe.naturalHeight) {
        setNaturalAspect(probe.naturalWidth / probe.naturalHeight);
      }
    };
    probe.src = baseAssetUrl; // 画布本来就要加载同一 URL,这里命中缓存,不多下一份
    return () => {
      probe.onload = null;
    };
  }, [baseAssetUrl]);

  // Canvas 宽高比:asset 真实像素 → 实图量出来的 → task.ratio → 1.0 兜底
  const aspectRatio: number =
    (asset.actualWidth && asset.actualHeight ? asset.actualWidth / asset.actualHeight : null) ??
    naturalAspect ??
    aspectFromRatio(task.ratio) ??
    1;

  /* 拖拽画布(画面调整 tab)的盒子驱动取景。PosterCanvas 自己会量,这一条只服务
     DragOverlayCanvas 那条分支 —— 两条分支必须收在同一个框里,否则切 tab 时图会跳一下。 */
  const dragFit = useFitBox(aspectRatio);

  return (
    /* ── Stage | Dock(2026-08-07 版面重构)────────────────────────────────────
       ★五种形态(主图/组图/套图/宽图/长图)统一成同一副骨架:左看图台、右操作台。
         改前宽图走单列、长图走上下两块、其余走左右 —— 操作台的位置对用户是随机的。
       ★整块吃父级给的高度(`h-full min-h-0`):Stage 自己不长,画布按框收;
         Dock 自己滚,主按钮焊在列底。父级不给高度时退化成自然高(窄档堆叠),不会炸。 */
    <div className="grid h-full min-h-0 gap-4 lg:grid-cols-[minmax(0,1fr)_400px]">
      {/* ── Stage 看图台 ── */}
      <div className="flex min-h-0 flex-col gap-3">
        <div className="flex items-center gap-2">
          <VersionSwitcher versions={versions} selectedId={asset.id} onSelect={onSelectVersion} />
          {/* 张与张之间翻页(C-02)。只在真有多张时出现 —— 单张任务不该多两个灰按钮。 */}
          {siblings.length > 1 && (
            <div className="ml-auto flex items-center gap-1 text-xs text-muted-foreground">
              <Button
                variant="ghost"
                size="sm"
                className="h-7 gap-1 px-2"
                disabled={!prevSibling}
                aria-label="上一张"
                onClick={() => prevSibling && onSelectVersion(prevSibling.id)}
              >
                <ChevronLeft className="h-4 w-4" />
                上一张
              </Button>
              <span className="tabular-nums">
                {siblingPos + 1}/{siblings.length}
              </span>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 gap-1 px-2"
                disabled={!nextSibling}
                aria-label="下一张"
                onClick={() => nextSibling && onSelectVersion(nextSibling.id)}
              >
                下一张
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>
        <Card className="flex min-h-0 flex-1 flex-col">
          <CardContent className="flex min-h-0 flex-1 flex-col p-3">
            {/* 2026-07-28 W2:组图角标 —— 三张组图各讲一个卖点,不写出来用户分不清哪张是哪张 */}
            {stageBadge && (
              <div className="mb-2 inline-flex max-w-full items-center gap-1.5 rounded-md border border-primary/30 bg-primary/10 px-2.5 py-1 text-xs font-medium text-primary">
                <Flame className="h-3.5 w-3.5 shrink-0" />
                <span className="truncate" title={stageBadge}>
                  {stageBadge}
                </span>
              </div>
            )}
            {missingBase && (
              <div className="mb-3 rounded-md border border-warning/40 bg-warning/10 p-3 text-xs text-warning">
                {t("editor.missingBase")}
              </div>
            )}
            {/* ★取景区:吃掉 Card 的剩余高度,画布在里面居中。这一层就是 useFitBox 量的那个框。 */}
            <div
              ref={dragFit.ref}
              className="flex min-h-0 flex-1 items-center justify-center overflow-hidden"
            >
              {tab === "appearance" && appearanceBaseUrl ? (
                // V6 fix4: 画面 tab 内嵌 DragOverlayCanvas — 拖拽 + 滤镜合一,filter 通过外层 CSS
                // (brightness/contrast/saturation/warmth) 实时预览,save 走 V6 PATCH 一次落两个。
                /* ★宽度显式给 px:DragOverlayCanvas 的底图是 `w-full`,拖拽坐标又是按实测 rect
                   归一化(0~1)的 —— 收窄容器不会错位,只是整体等比缩小。 */
                <div
                  style={{
                    filter: comparing ? "none" : filterToCss(previewFilter),
                    transition: "filter 80ms linear",
                    width: dragFit.width ?? "100%",
                  }}
                >
                  <DragOverlayCanvas
                    baseImageUrl={appearanceBaseUrl ?? undefined}
                    brandAssets={{
                      // V37 — override 优先;否则 task 原生 brand URL。
                      logoUrl:
                        resolveLogoPreviewUrl(v6Overlay, brandAssets) ?? task.logoUrl ?? undefined,
                      qrUrl:
                        resolveQrPreviewUrl(v6Overlay, brandAssets) ??
                        urlQrDataUrl ??
                        task.qrCodeUrl ??
                        undefined,
                      // V6 fix4 (later): 租户没上传就不渲染拖拽框,而不是 fallback 示例图。
                      // sample placeholder 是给 admin 模板编辑用的(那场景下 brand 还没绑租户)。
                      fallbackLogo: false,
                      fallbackQr: false,
                    }}
                    value={comparing ? persistedV6 : v6Overlay}
                    onChange={setV6Overlay}
                    readOnly={comparing}
                  />
                </div>
              ) : (
                <PosterCanvas
                  baseUrl={baseUrl}
                  templateAnchors={template.anchors}
                  overlay={previewOverlay}
                  filter={previewFilter}
                  brand={canvasBrand}
                  aspectRatio={aspectRatio}
                  /* 任务 17:这里是「看图」的主位置,给查看缩放。拖拽编辑那条分支
                     (DragOverlayCanvas)不给 —— 缩放会和拖拽的坐标换算打架,
                     而那个 tab 的任务是摆位置不是看细节。 */
                  zoomable
                  /* 2026-08-07:盒子驱动取景 —— 画布按 Stage 的框收,不再撑满宽度。 */
                  fit
                />
              )}
            </div>

            {/* ── Stage 底栏:三样东西压成一行(shrink-0,永不参与抢高度)──────────
                改前是三块各占一行(对比原图 / 看原图 / 右键提示),纵向吃掉 ~90px ——
                而 Stage 的高度是从图身上省下来的,一行文字就是一行图。 */}
            <div className="mt-2 flex shrink-0 flex-wrap items-center gap-x-3 gap-y-1">
              {/* V26.4 (2026-05-14) — "按住对比原图" 跟"画面调整"语义绑定 (实时滤镜
                  /拖拽 vs 原图);AI 改图 / AI 重画 tab 是异步重生,不需要对比,且 comparing
                  state 只清滤镜不清 i2i/t2i 结果。隐藏。 */}
              {tab === "appearance" && (
                <Button
                  variant="outline"
                  size="sm"
                  onMouseDown={() => setComparing(true)}
                  onMouseUp={() => setComparing(false)}
                  onMouseLeave={() => setComparing(false)}
                  onTouchStart={() => setComparing(true)}
                  onTouchEnd={() => setComparing(false)}
                  className={cn("h-7 gap-1 text-xs", comparing && "bg-warning/10 text-warning")}
                >
                  <Eye className="h-3 w-3" />
                  {comparing ? t("editor.showingOrigin") : t("editor.holdToCompare")}
                </Button>
              )}
              {/* BP-01:主画布实测只渲染到 38%~44%,用户看不清自己花积分生成的东西。
                  ★不把整块 canvas 做成点击放大:它是带拖拽(logo/QR)+「按住对比原图」的**编辑**画布,
                  再叠一个 click 会和那两个交互抢事件。给一个明确入口即可。 */}
              {baseUrl && (
                <>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 gap-1 px-2 text-xs"
                    onClick={() => setViewerOpen(true)}
                    data-testid="poster-open-viewer"
                  >
                    <Eye className="h-3 w-3" />
                    看原图
                  </Button>
                  {/* ★下载搬到看图台底栏(2026-08-07):以前它**只**长在成品条的卡片上,
                      而成品条在单图任务里是同一张图的第二次出现 —— 为了把那 185px 还给画面,
                      单图任务不再渲成品条,下载就必须先在这儿有个家。 */}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 gap-1 px-2 text-xs"
                    onClick={() => void downloadCurrentAsset()}
                    data-testid="poster-download-current"
                  >
                    <Download className="h-3 w-3" />
                    下载这张
                  </Button>
                  <MediaPreviewLightbox
                    open={viewerOpen}
                    onOpenChange={setViewerOpen}
                    items={[{ url: baseUrl, kind: "image", title: "原图(底图原始像素)" }]}
                    index={0}
                    onIndexChange={() => {}}
                  />
                </>
              )}
              {tab === "appearance" && (
                <span className="text-2xs text-foreground/70">{t("editor.pixelHint")}</span>
              )}
              {/* 右键存图引导 — canvas 是多层 HTML 合成,浏览器原生右键只能存底层 base.png(无 logo/QR/滤镜)。
                  完整海报必须走「下载」按钮(后端 /compose 出真合成 PNG)。所有 tab 都需要这个提示。 */}
              <span className="text-2xs leading-snug text-muted-foreground">
                {t("editor.rightClickHint")}
              </span>
            </div>
          </CardContent>
        </Card>
        {/* Tray:成品条贴在主画面正下方 —— 切图零滚动,且六张画幅能摆成一行比 */}
        {tray && <div className="shrink-0">{tray}</div>}
      </div>

      {/* ── Dock 操作台:tab 固定在顶、内容自滚、主按钮焊在列底 ── */}
      <div className="flex min-h-0 flex-col gap-3">
        <div className="flex shrink-0 gap-1 rounded-lg border bg-muted/30 p-1">
          {(
            [
              { id: "appearance", label: t("editor.tabAppearance") },
              { id: "edit", label: t("editor.tabEdit") },
              { id: "redraw", label: t("editor.tabRedraw") },
            ] as const
          ).map((tab2) => (
            <button
              key={tab2.id}
              type="button"
              onClick={() => setTab(tab2.id)}
              className={cn(
                "flex flex-1 items-center justify-center gap-1 rounded-md px-3 py-1.5 text-sm transition-colors",
                tab === tab2.id
                  ? "bg-background font-medium shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              )}
            >
              {tab2.label}
            </button>
          ))}
        </div>

        {/* ★内容自滚(min-h-0 + flex-1):属性面板天然比列高,不给它自己的滚动区就会把
            下面的保存按钮顶出视口 —— 那正是「主 CTA 要滚才够得着」的老病。 */}
        <div className="min-h-0 flex-1 space-y-3 overflow-y-auto pr-1">
          <Card>
            <CardContent className="space-y-4 p-4">
              {tab === "appearance" && (
                <>
                  <div className="flex items-start gap-2 rounded bg-info/10 p-2 text-xs text-info">
                    <Info className="mt-0.5 h-3 w-3 shrink-0" />
                    <span>{t("editor.appearanceHint")}</span>
                  </div>
                  <V6DragPanel
                    value={v6Overlay}
                    onChange={setV6Overlay}
                    hasBrandLogo={hasBrandLogo}
                    hasBrandQr={hasBrandQr}
                    baked={baked}
                    canvasAspect={aspectRatio}
                  />
                  <div className="border-t border-border/40 pt-3">
                    <AppearanceTab filter={filter} onChange={setFilter} />
                  </div>
                </>
              )}
              {tab === "edit" && (
                <I2IEditTab
                  busy={regenMut.isPending}
                  // Phase D — 新字段 baseUrl 优先,旧 baseImageUrl/fullImageUrl 兜底
                  hasBaseImage={!!asset.baseUrl}
                  credits={regenCredits}
                  onSubmit={(body) => regenMut.mutate({ ...body, mode: "edit" })}
                />
              )}
              {tab === "redraw" && (
                <RegenerateTab
                  busy={regenMut.isPending}
                  credits={regenCredits}
                  onRegenerate={(body) => regenMut.mutate({ ...body, mode: "semantic" })}
                />
              )}
            </CardContent>
          </Card>

          {/* 0713 批量品牌变体:同一张图 × 多组 logo/二维码,一行一张(渠道场景批量出图)。
              次级动作,跟着内容一起滚 —— 焊在列底的位置留给主 CTA。 */}
          {tab === "appearance" && (
            <>
              {/* 1006:品牌变体 = 在本张底图上再叠 logo/二维码;出图时已烙过的图上再叠就是第二个,不给入口。 */}
              <Button
                variant="outline"
                size="sm"
                className="w-full gap-1"
                disabled={baked.logo || baked.qr}
                data-testid="brand-variants-open"
                onClick={() => setVariantsOpen(true)}
              >
                <Layers className="h-3 w-3" />
                {t("variants.openButton")}
              </Button>
              {(baked.logo || baked.qr) && (
                <p className="text-2xs text-muted-foreground">{t("editor.variantsBakedHint")}</p>
              )}
              <BrandVariantsDialog
                open={variantsOpen}
                onOpenChange={setVariantsOpen}
                taskId={task.id}
                assetId={asset.id}
                v6Overlay={v6Overlay}
                onDone={async () => {
                  await onRefresh?.();
                  queryClient.invalidateQueries({ queryKey: ["poster-assets", task.id] });
                }}
              />
            </>
          )}
        </div>

        {/* 画面 Tab 显示保存按钮 — 一次落 overlay + filter,走 V6 PATCH /overlay。
            ★shrink-0 焊在列底:改前它跟在属性表后面,表一长就被顶到视口外。 */}
        {tab === "appearance" && (
          <div className="flex shrink-0 gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={(!v6Dirty && !dirty) || combinedSaveMut.isPending}
              onClick={() => {
                setV6Overlay(persistedV6);
                handleResetAdjustments();
              }}
              className="gap-1"
            >
              <RotateCcw className="h-3 w-3" />
              {t("editor.reset")}
            </Button>
            <Button
              variant="default"
              size="sm"
              disabled={(!v6Dirty && !dirty) || combinedSaveMut.isPending}
              onClick={() => combinedSaveMut.mutate()}
              className="flex-1 gap-1"
            >
              {combinedSaveMut.isPending ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <Save className="h-3 w-3" />
              )}
              {t("editor.saveRecompose")}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * V6 fix4 (2026-05-12): 拖拽 tab 的右侧调整面板。
 *
 * V6 fix4 (later): 加 brand 实际有没有 logo / QR 的标志 — 没上传就明确告诉用户
 * "在这张图上不会叠加 X",而不是默默 fallback 到示例图。每段单独可以移除/恢复,
 * 兼容 "上传了但这张不要" 的场景。
 */
function V6DragPanel({
  value,
  onChange,
  hasBrandLogo,
  hasBrandQr,
  baked = { logo: false, qr: false },
  canvasAspect,
}: {
  value: OverlayConfigV6;
  onChange: (next: OverlayConfigV6) => void;
  hasBrandLogo: boolean;
  hasBrandQr: boolean;
  /** 出图时已叠在画面上的(见 bakedOverlayOf)—— 优先于一切:只如实说,不给框、不给「挂上/添加回」(再叠=第二个)。 */
  baked?: { logo: boolean; qr: boolean };
  canvasAspect: number;
}) {
  const { t } = useTranslation("poster");
  const showLogo = value.logo != null;
  const showQr = value.qr != null;
  // V37 (2026-05-18) — task 创建时没选 brand,详情页里也允许临时挂 logo / QR(per-asset override)。
  // showLogo + hasBrandLogo 四象限:
  //   - showLogo            → 渲染编辑器
  //   - !showLogo && hasBrandLogo → "+添加回 Logo"(task 有 brand,从 task.brand 取 URL)
  //   - !showLogo && !hasBrandLogo → "+挂上 Logo"(从租户 brand_assets 选,写 logoUploadId override)
  //   QR 同理。logo / QR 各自独立判断,允许「task 有 logo 没 QR」混合情况。
  const needsBrandHint = !hasBrandLogo || !hasBrandQr;
  return (
    <div className="space-y-3 text-xs">
      <p className="text-2xs text-muted-foreground">{t("editor.dragHint")}</p>
      {showLogo && value.logo ? (
        <fieldset className="space-y-1 rounded border border-border/60 p-2">
          <legend className="flex items-center gap-2 px-1">
            <span className="font-medium">Logo</span>
            <button
              type="button"
              onClick={() => onChange({ ...value, logo: null, logoUploadId: null })}
              className="text-2xs text-muted-foreground hover:text-destructive"
              title={t("editor.noLogoOnThis")}
            >
              ✕ 不要
            </button>
          </legend>
          <V6NumberInput
            label="x"
            value={value.logo.x}
            onChange={(v) => onChange({ ...value, logo: { ...value.logo!, x: v } })}
          />
          <V6NumberInput
            label="y"
            value={value.logo.y}
            onChange={(v) => onChange({ ...value, logo: { ...value.logo!, y: v } })}
          />
          <V6NumberInput
            label="w"
            value={value.logo.w}
            onChange={(v) => onChange({ ...value, logo: { ...value.logo!, w: v } })}
          />
          <V6NumberInput
            label="h"
            value={value.logo.h}
            onChange={(v) => onChange({ ...value, logo: { ...value.logo!, h: v } })}
          />
          <V6NumberInput
            label="旋转°"
            value={value.logo.rotation ?? 0}
            onChange={(v) => onChange({ ...value, logo: { ...value.logo!, rotation: v } })}
            max={360}
            step={1}
          />
        </fieldset>
      ) : baked.logo ? (
        <p
          data-testid="v6-logo-baked"
          className="rounded border border-border/60 px-2 py-1.5 text-2xs text-muted-foreground"
        >
          {t("editor.logoBakedIn")}
        </p>
      ) : hasBrandLogo ? (
        <button
          type="button"
          onClick={() => onChange({ ...value, logo: DEFAULT_OVERLAY.logo })}
          className="w-full rounded border border-dashed border-border/60 px-2 py-1.5 text-2xs text-muted-foreground hover:border-primary/50 hover:text-foreground"
        >
          {t("editor.addBackLogo")}
        </button>
      ) : (
        <AttachLogoButton value={value} onChange={onChange} canvasAspect={canvasAspect} />
      )}
      {showQr && value.qr ? (
        <fieldset className="space-y-1 rounded border border-border/60 p-2">
          <legend className="flex items-center gap-2 px-1">
            <span className="font-medium">{t("freeCreate.qrcode")}</span>
            <button
              type="button"
              onClick={() =>
                onChange({
                  ...value,
                  qr: null,
                  qrCodeMode: null,
                  qrCodeUploadId: null,
                  qrCodeUrl: null,
                })
              }
              className="text-2xs text-muted-foreground hover:text-destructive"
              title={t("editor.noQrOnThis")}
            >
              ✕ 不要
            </button>
          </legend>
          <V6NumberInput
            label="x"
            value={value.qr.x}
            onChange={(v) => onChange({ ...value, qr: { ...value.qr!, x: v } })}
          />
          <V6NumberInput
            label="y"
            value={value.qr.y}
            onChange={(v) => onChange({ ...value, qr: { ...value.qr!, y: v } })}
          />
          <V6NumberInput
            label="w"
            value={value.qr.w}
            onChange={(v) => onChange({ ...value, qr: { ...value.qr!, w: v } })}
          />
          <V6NumberInput
            label="h"
            value={value.qr.h}
            onChange={(v) => onChange({ ...value, qr: { ...value.qr!, h: v } })}
          />
        </fieldset>
      ) : baked.qr ? (
        <p
          data-testid="v6-qr-baked"
          className="rounded border border-border/60 px-2 py-1.5 text-2xs text-muted-foreground"
        >
          {t("editor.qrBakedIn")}
        </p>
      ) : hasBrandQr ? (
        <button
          type="button"
          onClick={() => onChange({ ...value, qr: DEFAULT_OVERLAY.qr })}
          className="w-full rounded border border-dashed border-border/60 px-2 py-1.5 text-2xs text-muted-foreground hover:border-primary/50 hover:text-foreground"
        >
          {t("editor.addBackQr")}
        </button>
      ) : (
        <AttachQrButton value={value} onChange={onChange} canvasAspect={canvasAspect} />
      )}
      {needsBrandHint && (
        <p className="text-2xs text-muted-foreground/80">
          {t("editor.noBrandManageHint")}{" "}
          <Link to="/tenant-admin/brand-assets" className="text-primary hover:underline">
            {t("editor.gotoBrandAssets")}
          </Link>
        </p>
      )}
    </div>
  );
}

/**
 * V37 — 详情页临时挂载 brand asset 的按钮。
 * 0 个 → toast 引导去品牌资产页 / 1 个 → 一键挂 default / 多个 → popover 缩略图 selector。
 * 落点用 pickDefaultLogoPosition / pickDefaultQrPosition 算,带 canvasAspect。
 */
function V6NumberInput({
  label,
  value,
  onChange,
  max = 1,
  step = 0.01,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  max?: number;
  step?: number;
}) {
  return (
    <label className="flex items-center justify-between gap-2">
      <span className="text-muted-foreground">{label}</span>
      <input
        type="number"
        min={0}
        max={max}
        step={step}
        value={Number(value.toFixed(3))}
        onChange={(e) => onChange(Number(e.target.value))}
        className="h-6 w-20 rounded border border-border/60 bg-transparent px-1 text-right text-xs"
      />
    </label>
  );
}
